import React, {FC, useState, useEffect} from "react"
import PropTypes from "prop-types"
import {Row} from "react-bootstrap"

import History from "@irati/utils/history"
import withBranding, {WithBranding} from "@irati/brand/withBranding"
import Flex from "@irati/ui/src/Flex"
import {Checkbox, ValidationButton} from "@irati/ui/src/FormItem"
import Spacing from "@irati/ui/src/Spacing"
import {H3, H6} from "@irati/ui/src/Typography"
import Validation from "@irati/utils/validation"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import Link from "@irati/ui/src/Link"
import {Office} from "@irati-web/fields/user"
import {officesForTenant} from "@irati/identity/utils/tenant/hasMultipleOffices"
import withAuth, {WithAuth} from "@irati/auth/helpers/auth/withAuth"
import userService from "@irati/identity/services/users"
import teamService from "@irati/identity/services/teams"
import marketingService from "@irati/marketing/services/marketing"
import onboardingService from "@irati/onboarding/services/onboarding"
import {MarketingSubscriptionState} from "@irati/types"
import authStream from "@irati/auth/streams/auth"
import OnboardingSteps from "@irati/onboarding/enums/Step"
import authentication from "@irati/auth"
import adminTracking from "@irati-tracking/admin"

import {StyledForm, ResponsiveImg} from "./styled"

interface Context {
  apps: {[key: string]: string}
}

interface Props extends WithBranding, WithAuth {}

const TermsAndConditions: FC<Props> = ({branding, auth}, {apps}: Context) => {
  const [termsAndConditionsChecked, setTermsAndConditionsChecked] = useState(false)
  const [marketingPreferencesChecked, setMarketingPreferencesChecked] = useState(true)
  const [showRegions, setShowRegions] = useState(false)
  const [office, setOffice] = useState<string | null>(null)
  const [saving, setSaving] = useState<Promise<any> | null>(null)

  const {formatMessage} = useIntl()
  const agreeSchema = Validation.bool().oneOf([true], "onboarding.termsAndConditions.checkBox.validation.agreeSchema")

  useEffect(() => {
    adminTracking.enterTermsAndConditions()
    officesForTenant().then((offices) => {
      if (offices.length > 1) {
        const billingUnitIds = offices.map(({uuid}) => uuid)
        const teamIds = auth.user.teamIds
        setShowRegions(!teamIds.some((id: string) => billingUnitIds.includes(id)))
      }
    })
  }, [])

  const handleActivate = () => {
    const {user} = auth
    const data: UserInput = {
      uuid: user.uuid,
      dateOfBirth: user.dateOfBirth
    }

    const updateUserPromise = userService.update({user: data})
    let promises = [updateUserPromise]

    if (office) {
      promises = [updateUserPromise, teamService.setMyBillingUnit(office)]
    }

    if (!marketingPreferencesChecked) {
      const updateMarketingPreferences = marketingService.getProfile({userId: user.uuid}).then(({signature}) => {
        return marketingService.updateProfile({
          uuid: user.uuid,
          signature,
          settings: {
            emailSubscribed: MarketingSubscriptionState.Unsubscribed,
            pushSubscribed: MarketingSubscriptionState.Unsubscribed
          }
        })
      })
      promises = [...promises, updateMarketingPreferences]
    }

    const submit = Promise.all(promises).then(() => {
      return authStream.refresh().then(() => {
        return onboardingService.completeStep(user, OnboardingSteps.ACCEPT_GDPR).then(() => {
          adminTracking.completeActivation()
          setSaving(null)
          if (authentication.isGlobalAdmin()) {
            History.push({pathname: "/"}, apps.ADMIN)
          } else {
            History.push("/")
          }
        })
      })
    })
    setSaving(submit)
  }

  return (
    <Flex direction="column" alignItems="center">
      {branding?.businessLogoUrl && (
        <Spacing marginTop={2} marginBottom={2}>
          <img width="150px" src={branding.businessLogoUrl} />
        </Spacing>
      )}
      <ResponsiveImg src={formatMessage({id: "platform.oneTimePassword.termsAndConditions.img"})} />
      <H3 marginTop marginBottom={2}>
        <FormattedMessage id="platform.oneTimePassword.termsAndConditions.title" />
      </H3>
      {showRegions && (
        <Flex width="100%" direction="column" marginBottom={2}>
          <Row>
            <Office value={office} onChange={setOffice} />
          </Row>
        </Flex>
      )}
      <StyledForm>
        <Flex width="100%">
          <Checkbox
            name="marketingPreferences"
            onChange={() => setMarketingPreferencesChecked(!marketingPreferencesChecked)}
            checked={marketingPreferencesChecked}>
            <H6 display="inline">
              <FormattedMessage id="platform.oneTimePassword.termsAndConditions.marketingPreferences.checkbox" />
            </H6>
          </Checkbox>
        </Flex>
        <Checkbox
          name="termsAndConditions"
          onChange={() => setTermsAndConditionsChecked(!termsAndConditionsChecked)}
          checked={termsAndConditionsChecked}
          validation={agreeSchema}>
          <H6 display="inline">
            <FormattedMessage id="platform.oneTimePassword.termsAndConditions.checkbox.title" />
          </H6>
          <Link href={formatMessage({id: "platform.oneTimePassword.termsAndConditions.checkbox.href"})} target="_blank">
            <FormattedMessage id="platform.oneTimePassword.termsAndConditions.checkbox.text" />
          </Link>
        </Checkbox>
        <Flex justifyContent="flex-end" width="100%">
          <ValidationButton primary mobileFull onClick={handleActivate} load={saving}>
            <FormattedMessage id="platform.oneTimePassword.termsAndConditions.btn" />
          </ValidationButton>
        </Flex>
      </StyledForm>
    </Flex>
  )
}

TermsAndConditions.contextTypes = {
  apps: PropTypes.object.isRequired
}

export default withBranding(withAuth(TermsAndConditions))
